import { Cartographic } from "./Cartographic";
import { MathUtils } from "./MathUtils";
/**
 * Represents a rectangular region in a geographic coordinate system, stored as latitude/longitude in radians.
 *
 * **Requirements**
 *
 * Minimum point must always be <= maximum point:
 *   - Rectangle.west must be <= Rectangle.east
 *   - Rectangle.south must be <= Rectangle.south
 *
 * Longitude must not wrap around
 *   - The following must hold true: -PI >= Rectangle.west >= PI
 *   - The following must hold true: -PI >= Rectangle.east >= PI
 *
 * Latitude must not wrap around
 *   - The following must hold true: -PI/2 >= Rectangle.south >= PI/2
 *   - The following must hold true: -PI/2 >= Rectangle.north >= PI/2
 *
 * A 'Rectangle' instance will happily store a rectangle that violates any of these properties, but using any of the
 * functions on 'Rectangle' may give erroneous output as they expect to operate on only legal rectangles.
 */
export class Rectangle {
    constructor(west = 0, south = 0, east = 0, north = 0) {
        this.west = west;
        this.south = south;
        this.east = east;
        this.north = north;
    }
    /**
     * Returns the width of the rectangle in radians
     */
    get width() {
        return this.east - this.west;
    }
    /**
     * Returns the height of the rectangle in radians
     */
    get height() {
        return this.north - this.south;
    }
    /**
     * Returns a 'Cartographic' that represents the southwestern-most point of the rectangle
     */
    get southwest() {
        return new Cartographic(this.west, this.south);
    }
    /**
     * Returns a 'Cartographic' that represents the southeastern-most point of the rectangle
     */
    get southeast() {
        return new Cartographic(this.east, this.south);
    }
    /**
     * Returns a 'Cartographic' that represents the northwestern-most point of the rectangle
     */
    get northwest() {
        return new Cartographic(this.west, this.north);
    }
    /**
     * Returns a 'Cartographic' that represents the northeastern-most point of the rectangle
     */
    get northeast() {
        return new Cartographic(this.east, this.north);
    }
    /**
     * Returns a 'Cartographic' that represents the center, or midpoint, of the triangle
     */
    get midpoint() {
        return new Cartographic((this.west + this.east) / 2, (this.south + this.north) / 2);
    }
    /**
     * Sets each value on this 'Rectangle', returning a reference to 'this'
     * @param west see Rectangle.west
     * @param south see Rectangle.south
     * @param east see Rectangle.east
     * @param north see Rectangle.north
     */
    set(west, south, east, north) {
        this.west = west;
        this.south = south;
        this.east = east;
        this.north = north;
        return this;
    }
    /**
     * Sets the values on 'this' to the provided values. The input values in degrees will be converted to radians.
     * @param west see Rectangle.west
     * @param south see Rectangle.south
     * @param east see Rectangle.east
     * @param north see Rectangle.north
     */
    fromDegrees(west, south, east, north) {
        this.west = MathUtils.degreesToRadians(west);
        this.south = MathUtils.degreesToRadians(south);
        this.east = MathUtils.degreesToRadians(east);
        this.north = MathUtils.degreesToRadians(north);
        return this;
    }
    /**
     * Set the values on 'this' to the values on 'other'. Returns a reference to 'this'
     * @param other
     */
    copy(other) {
        this.west = other.west;
        this.south = other.south;
        this.east = other.east;
        this.north = other.north;
        return this;
    }
    /**
     * Constructs a new 'Rectangle' instance with the same values as 'this'.
     */
    clone() {
        return new Rectangle().copy(this);
    }
    /**
     * Returns true if the cartographic is on or inside the rectangle, false otherwise.
     *
     * @param cartographic The cartographic to test.
     * @returns true if the provided cartographic is inside the rectangle, false otherwise.
     */
    contains(cartographic) {
        const longitude = cartographic.longitude;
        const latitude = cartographic.latitude;
        return longitude >= this.west && longitude <= this.east && latitude >= this.south && latitude <= this.north;
    }
    /**
     * Returns true if the provided rectangle intersects with this rectangle, otherwise returns false.
     * @param other The region to test.
     */
    intersects(other) {
        const longIntersect = this.west <= other.east || this.east >= other.west;
        const latIntersect = this.south <= other.north || this.north >= other.south;
        return longIntersect && latIntersect;
    }
    /**
     * Returns the intersection of 'this' and 'other'. This returns a new rectangle that represents the area that the
     * two rectangles overlap.
     *
     * This function will produce a malformed 'Rectangle' if the two input rectangles do not intersect. It is
     * recommended to call 'Rectangle.intersects' first to ensure they do intersect. The resulting triangle will have
     * negative width or height.
     *
     * e.g: The function will return a rectangle that represents 'C' in the below image.
     *
     *     ┌────────┐
     *     │      B │
     * ┌───┼────┐   │
     * │   │ C  │   │
     * │   │    │   │
     * │   └────┼───┘
     * │ A      │
     * └────────┘
     *
     * @param other
     * @param result
     */
    intersection(other, result = new Rectangle()) {
        result.west = Math.max(this.west, other.west);
        result.south = Math.max(this.south, other.south);
        result.east = Math.min(this.east, other.east);
        result.north = Math.min(this.north, other.north);
        return result;
    }
    /**
     * Returns the union of 'this' and 'other'. This returns a new rectangle that fully contains both 'this' and 'other'
     * within itself.
     *
     * This function will produce a malformed 'Rectangle' if the two input rectangles do not intersect. It is
     * recommended to call 'Rectangle.intersects' first to ensure they do intersect. The resulting triangle will have
     * negative width or height.
     *
     * e.g: With the given input:
     *
     *     ┌────────┐
     *     │      B │
     * ┌───┼────┐   │
     * │   │    │   │
     * │   │    │   │
     * │   └────┼───┘
     * │ A      │
     * └────────┘
     * A box that fully contains A and B is returned
     * ┌───┬────────┐
     * │   │      B │
     * ├───┼────┐   │
     * │   │    │   │
     * │   │    │   │
     * │   └────┼───┤
     * │ A      │   │
     * └────────┴───┘
     * @param other
     * @param result
     */
    union(other, result = new Rectangle()) {
        result.west = Math.min(this.west, other.west);
        result.south = Math.min(this.south, other.south);
        result.east = Math.max(this.east, other.east);
        result.north = Math.max(this.north, other.north);
        return result;
    }
    /**
     * Clips a cartographic point against this rectangle. This will clamp the point's latitude and longitude so that the
     * output point is inside the rectangle being clipped against
     * @param point
     * @param result
     */
    clipPoint(point, result = new Cartographic()) {
        result.longitude = Math.max(this.west, Math.min(point.longitude, this.east));
        result.latitude = Math.max(this.south, Math.min(point.latitude, this.north));
        return result;
    }
    /**
     * Returns true if 'this' region is a superset of the 'other' region. That is, 'other' is entirely contained within
     * 'this'
     * @param other The other region to test
     */
    isSupersetOf(other) {
        // Simple separating axis test. Prove that 'this' is a superset along both axis individually. Proving both axis
        // proves 'this' as a superset.
        const longSuperset = this.west <= other.west && this.east >= other.east;
        const latSuperset = this.south <= other.south && this.north >= other.north;
        return longSuperset && latSuperset;
    }
    /**
     * Returns true if 'this' region is a subset of the 'other' region. That is, 'other' entire contains 'this' within
     * itself.
     * @param other
     */
    isSubsetOf(other) {
        // Subset can be trivially implemented as a superset test with the test inputs inverted
        // (i.e a.isSubset(b) == b.isSuperset(b))
        return other.isSupersetOf(this);
    }
    /**
     * Returns true if 'this' region exactly equals the 'other' region. This uses direct floating point equals
     * comparison without any epsilon.
     * @param other
     */
    equals(other) {
        return (this.west === other.west &&
            this.east === other.east &&
            this.south === other.south &&
            this.north === other.north);
    }
    /**
     * Returns true if 'this' region exactly equals the 'other' region, within the given range 'epsilon'.
     *
     * Sometimes an 'epsilon' is needed in floating point comparisons as numerical error and limited precision means
     * that some operations will produce slightly incorrect results. e.g. Using strict equality for '1.1 - 0.9 == 0.2'
     * will return false under IEE754 FP64. An epsilon adds an 'allowed error' to catch these kinds of errors.
     * @param other
     * @param epsilon
     */
    equalsEpsilon(other, epsilon) {
        // Simple epsilon is fine here as Rectangle will only use small floats (-Math.PI >= v >= Math.PI)
        return (Math.abs(this.west - other.west) <= epsilon &&
            Math.abs(this.east - other.east) <= epsilon &&
            Math.abs(this.south - other.south) <= epsilon &&
            Math.abs(this.north - other.north) <= epsilon);
    }
    /**
     * Returns whether this rectangle is a legally defined rectangle.
     *
     * See 'Rectangle' docs for definition of 'legal'
     *
     * @see Rectangle
     */
    isLegal() {
        const PI_ON_TWO = Math.PI / 2;
        if (this.west > this.east)
            return false;
        if (this.south > this.north)
            return false;
        if (this.west < -Math.PI || this.west > Math.PI)
            return false;
        if (this.east < -Math.PI || this.east > Math.PI)
            return false;
        if (this.south < -PI_ON_TWO || this.south > PI_ON_TWO)
            return false;
        if (this.north < -PI_ON_TWO || this.north > PI_ON_TWO)
            return false;
        return true;
    }
}
/**
 * Static instance with each field set to their maximal extent
 */
Rectangle.MAX = Object.freeze(new Rectangle(-Math.PI, -Math.PI / 2, Math.PI, Math.PI / 2));
